import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  FormControl,
  TextField,
  MenuItem,
  Paper,
  Popper,
  makeStyles
} from '@material-ui/core';

const useStyles = makeStyles(theme => ({
  selectRoot: {
    background: theme.palette.grey[50]
  },
  formControlRoot: {
    '& .MuiFilledInput-root.Mui-disabled': {
      background: theme.palette.grey[50]
    }
  },
  menuPaper: {
    overflowY: 'auto',
    overflowX: 'hidden',
    backgroundColor: theme.palette.background.paper,
    maxHeight: 550
  },
  menuItem: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '100%'
  },
  highlighted: {
    backgroundColor: theme.palette.action.hover
  }
}));

const FilterDropDown = ({ onSelect, items, id, label, noResultMessage }) => {
  const classes = useStyles();
  const [filter, setFilter] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef(null);
  const popperRef = useRef(null);
  const menuPaperRef = useRef(null);
  const itemRefs = useRef([]);
  const animationRef = useRef(null);

  const filteredItems = items.filter(item =>
    item.label.toLowerCase().includes(filter.toLowerCase())
  );

  const handleInputChange = event => {
    const { value } = event.target;
    setFilter(value);
    if (value === '') {
      onSelect('');
    }
    setIsOpen(true);
    setHighlightedIndex(-1);
  };

  const handleSelect = selectedValue => {
    onSelect(selectedValue);
    const selectedItem = items.find(item => item.value === selectedValue);
    setFilter(selectedItem ? selectedItem.label : '');
    setIsOpen(false);
  };

  const getMaxHeight = () => {
    const windowHeight = window.innerHeight;
    const popperPosition = inputRef.current
      ? inputRef.current.getBoundingClientRect().bottom
      : 0;
    const spaceBelow = windowHeight - popperPosition;
    return Math.min(spaceBelow - 16, 400);
  };

  const scrollIntoView = index => {
    if (index >= 0 && itemRefs.current[index] && menuPaperRef.current) {
      const highlightedItem = itemRefs.current[index];
      const menuRect = menuPaperRef.current.getBoundingClientRect();
      const itemRect = highlightedItem.getBoundingClientRect();

      if (itemRect.top < menuRect.top) {
        menuPaperRef.current.scrollTop -= menuRect.top - itemRect.top;
      } else if (itemRect.bottom > menuRect.bottom) {
        menuPaperRef.current.scrollTop += itemRect.bottom - menuRect.bottom;
      }
    }
  };

  const updateHighlightedIndex = useCallback(newIndex => {
    setHighlightedIndex(newIndex);
    scrollIntoView(newIndex);
  }, []);

  const handleArrowNavigation = useCallback(
    direction => {
      setHighlightedIndex(prevIndex => {
        const maxIndex = filteredItems.length - 1;
        let newIndex = prevIndex;

        if (direction === 'down') {
          newIndex = prevIndex < maxIndex ? prevIndex + 1 : 0;
        } else if (direction === 'up') {
          newIndex = prevIndex > 0 ? prevIndex - 1 : maxIndex;
        }

        updateHighlightedIndex(newIndex);
        return newIndex;
      });
    },
    [filteredItems.length, updateHighlightedIndex]
  );

  const handleKeyDown = event => {
    if (!isOpen) return;

    const { key } = event;
    if (key === 'ArrowDown' || key === 'ArrowUp') {
      event.preventDefault();

      if (!animationRef.current) {
        animationRef.current = requestAnimationFrame(() => {
          handleArrowNavigation(key === 'ArrowDown' ? 'down' : 'up');
          animationRef.current = null;
        });
      }
    } else if (key === 'Enter') {
      event.preventDefault();
      if (highlightedIndex >= 0 && highlightedIndex < filteredItems.length) {
        handleSelect(filteredItems[highlightedIndex].value);
      }
    }
  };

  useEffect(() => {
    const handleClickOutside = event => {
      if (
        popperRef.current &&
        !popperRef.current.contains(event.target) &&
        !inputRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isOpen]);

  return (
    <FormControl
      fullWidth
      margin="normal"
      classes={{ root: classes.formControlRoot }}
    >
      <TextField
        id={id}
        label={label}
        value={filter}
        onChange={handleInputChange}
        onFocus={() => setIsOpen(true)}
        classes={{ root: classes.selectRoot }}
        inputRef={inputRef}
        autoComplete="off"
        variant="filled"
        fullWidth
        onKeyDown={handleKeyDown}
      />
      <Popper
        open={isOpen}
        anchorEl={inputRef.current}
        placement="bottom-start"
        disablePortal={false}
        ref={popperRef}
        modifiers={{
          flip: { enabled: false },
          preventOverflow: { enabled: true, boundariesElement: 'viewport' },
          offset: {
            enabled: true,
            offset: '0, 12'
          }
        }}
        style={{
          zIndex: 1500,
          width: inputRef.current ? inputRef.current.offsetWidth : 'auto'
        }}
      >
        <Paper
          ref={menuPaperRef}
          className={classes.menuPaper}
          style={{ maxHeight: getMaxHeight(), width: '100%' }}
        >
          {filteredItems.length > 0 ? (
            filteredItems.map((item, index) => (
              <MenuItem
                key={item.value}
                onClick={e => {
                  e.stopPropagation();
                  handleSelect(item.value);
                }}
                className={`${classes.menuItem} ${
                  index === highlightedIndex ? classes.highlighted : ''
                }`}
                ref={el => (itemRefs.current[index] = el)}
              >
                {item.label}
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled>{noResultMessage}</MenuItem>
          )}
        </Paper>
      </Popper>
    </FormControl>
  );
};

export default FilterDropDown;
